// @ts-expect-error @headlessui/react incompatibility with node16 resolution
import {Disclosure} from '@headlessui/react';
import {useMemo, useState} from 'react';
import {IconMinus, IconPlus} from '~/components';
import {ProductSpecificationEntity} from '~/types/ProductSpecificationEntity';
import {Column} from '../Column.client';
import {Copy} from '../Copy.client';
import SanityImage from '../media/SanityImage.client';
import {Modal} from '../Modal.client';
import {Table} from '../Table.client';
import {ContentModalEntity} from '~/types';
import {DATA_TEST_IDS} from '~/lib/const';

export function ProductSpecifications({
  content,
  productSpecifications,
}: {
  content: string;
  productSpecifications?: ProductSpecificationEntity[];
}) {
  const items = useMemo(() => {
    try {
      return JSON.parse(content) as string[];
    } catch {
      console.error('Invalid JSON for ProductSpecifications');

      return undefined;
    }
  }, [content]);

  if (!items) {
    return null;
  }

  return (
    <div
      data-testid={DATA_TEST_IDS.PRODUCT_SPECIFICATIONS}
      className="lg:mt-6 border-y border-y-gray-300 py-5"
    >
      <Disclosure as="div" className="grid w-full md:hidden">
        {({open}) => (
          <>
            <Disclosure.Button className="text-left">
              <div className="flex justify-between">
                <Title />
                {open ? (
                  <IconMinus
                    data-testid={DATA_TEST_IDS.ICON('minus')}
                    className="mr-3"
                  />
                ) : (
                  <IconPlus
                    data-testid={DATA_TEST_IDS.ICON('plus')}
                    className="mr-3"
                  />
                )}
              </div>
            </Disclosure.Button>

            <Disclosure.Panel>
              <Content items={items} specifications={productSpecifications} />
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>

      <div className="hidden md:block">
        <Title />
        <Content items={items} specifications={productSpecifications} />
      </div>
    </div>
  );
}

function Title() {
  return (
    <div className="text-22/22 lg:text-28/28 font-header font-bold lg:font-extrabold">
      SPECIFICATIONS
    </div>
  );
}

function Content({
  items,
  specifications,
}: {
  items: string[];
  specifications?: ProductSpecificationEntity[];
}) {
  return (
    <div className="text-18/26">
      {items.map((item: string) => {
        const [key, value] = item.split(':');
        const specification = specifications?.find((spec) => spec.name === key);

        if (!specification) {
          return null;
        }

        return (
          <ContentSpecification
            key={item}
            name={key}
            value={value}
            specification={specification}
          />
        );
      })}
    </div>
  );
}

function ContentSpecification({
  name,
  value,
  specification,
}: {
  name: string;
  value: string;
  specification: ProductSpecificationEntity;
}) {
  const [open, setOpen] = useState(false);
  const values = useMemo(
    () => value?.split(',')?.map((v) => v?.trim()) ?? [],
    [value],
  );
  const images = useMemo(
    () =>
      specification?.values
        ?.filter((v) => v.name != null)
        .filter((v) => values.includes(v.name!))
        .sort((a, b) => {
          const indexA = values.indexOf(a.name!);
          const indexB = values.indexOf(b.name!);

          return indexA - indexB;
        }) ?? [],
    [specification.values, values],
  );
  const contentModal = specification?.values?.find(
    (v) => v.name === values[0],
  )?.contentModal;

  return (
    <>
      <div
        data-testid={DATA_TEST_IDS.CONTENT_SPECIFICATIONS}
        className="flex flex-col lg:flex-row mt-[30px] first:mt-0 lg:first:mt-3 border-t first:border-t-0 lg:first:border-t-1 border-t-gray-300 pt-[30px] lg:pt-5 items-stretch lg:items-start justify-between pb-3"
      >
        <div className="">
          <div className="text-16/20 tracking-wider font-bold font-header w-[262px] shrink-0">
            {name}
          </div>
          <div className="text-16/24 lg:text-18/26 mt-2.5 lg:mt-0 flex-1">
            <div className="max-w-[510px]">{values.join(', ')}</div>

            {contentModal && (
              <div className="flex lg:hidden mt-7 lg:mt-0">
                <MoreButton
                  contentModal={contentModal}
                  setOpen={() => setOpen(true)}
                />
              </div>
            )}

            {images && images.length > 0 && (
              <div className="flex flex-row gap-5 items-center mt-10 lg:mt-5 flex-wrap">
                {images.map((value) => {
                  if (!value || !value.image) {
                    return null;
                  }

                  return (
                    <SanityImage
                      key={value.image.asset?._ref}
                      className={
                        'rounded-[10px] md:rounded-none w-full h-full max-w-[150px]'
                      }
                      objectFit="cover"
                      crop={value.image.crop}
                      hotspot={value.image.hotspot}
                      layout="fill"
                      src={value.image.asset?._ref}
                      breakpoints={[[200, 200]]}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="hidden w-[120px] flex-shrink-0 justify-end lg:flex items-start">
          <MoreButton
            contentModal={contentModal}
            setOpen={() => setOpen(true)}
          />
        </div>
      </div>

      <Modal
        title={contentModal?.title ?? name}
        isOpen={open}
        onCloseModal={() => setOpen(false)}
        classNamePanel="w-[1272px] min-h-[833px] pl-5 md:pl-[131px] pr-5 py-5 md:pr-10 md:pt-10 md:pb-[131px]"
        classNameTitle="text-34/46 text-center md:text-left mt-[28px]"
      >
        <div className="md:pr-[53px] md:-ml-[38px] pt-1">
          {contentModal?.content?.map((content) => {
            if (!content || !content._type) {
              console.error(
                // @ts-ignore
                `Unknown component. type: ${content?._type} id: ${content?._id}`,
              );
              return null;
            }

            return (
              <div key={content.name}>
                {content._type === 'copy' && content?.copy && (
                  <Copy copy={content.copy} />
                )}
                {content._type === 'column' && <Column column={content} />}
                {content._type === 'tableInternal' && (
                  <div>
                    <div className="overflow-auto">
                      <Table table={content} />
                    </div>
                    {content.hideScrollText !== true && (
                      <div className="md:hidden text-16/26 text-gray-500 mt-1">
                        Scroll horizontally to see more.
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Modal>
    </>
  );
}

function MoreButton({
  contentModal,
  setOpen,
}: {
  contentModal?: ContentModalEntity;
  setOpen: () => void;
}) {
  if (contentModal) {
    return (
      <button
        data-testid={DATA_TEST_IDS.BUTTON('more')}
        type="button"
        className="text-16/20 font-header font-bold tracking-wider border-b-[3px] border-b-blue-500 flex items-center py-1 -mt-[4px] mr-[11px]"
        onClick={setOpen}
      >
        MORE
        <IconPlus className="ml-[11px] w-3 h-3" />
      </button>
    );
  }

  return null;
}
